import React from "react";
import LegalPage from "../../components/legal/LegalPage";

// Verbatim reproduction of https://www.tocahealth.com/privacy (as published;
// entity naming, wording, and source quirks intentionally preserved , any
// revision to the legal text itself is owned by the legal team, not here).
const SECTIONS = [
  {
    id: "welcome",
    heading: "Welcome",
    body: (
      <p>
        Welcome to Care Connection Networks, LLC, ("CCN"). This Privacy Policy
        and Notice of Privacy Practices ("Privacy Policy") tells you how we
        protect and use information that we gather or that you may submit
        through this website. This website and this Privacy Policy are subject
        to applicable U.S. federal and state laws and regulations. This Privacy
        Policy was last revised on the date listed below in the "Changes to
        Privacy Policy" paragraph. We may change this Privacy Policy at any
        time and for any reason. We encourage you to review this Privacy Policy
        each time you visit or submit information to the website. By using this
        website, you agree to the terms of the most recent version of this
        Privacy Policy. Please read our Terms of Use to understand the general
        rules about your use of this website. Except as written in any other
        disclaimers, policies, terms of use, or other notices on this website,
        this Privacy Policy and the Terms of Use are the complete agreement
        between you and CCN with respect to your use of this website. You may
        be subject to additional terms that may apply when you access
        particular services or materials on certain areas in this website, by
        following a link from this website or when you purchase any CCN
        product.
      </p>
    ),
  },
  {
    id: "introduction",
    heading: "Introduction",
    body: (
      <p>
        The information we receive, and how we use it, depends on what you do
        when visiting our website. We collect and use your non-personal
        information (information that is not identifiable to you personally)
        differently than your personal information.
      </p>
    ),
  },
  {
    id: "non-personal-info",
    heading: "What is Non-Personal Information?",
    body: (
      <>
        <p>
          Non-personal information is information we collect through this
          website that does not identify you as an individual person. It may
          include information such as the following:
        </p>
        <ul>
          <li>The type of web browser software you use</li>
          <li>The name of the domain from which you access the Internet</li>
          <li>
            The Internet address of the website from which you linked directly
            to our website
          </li>
          <li>The date and time you access our website</li>
          <li>Which pages you have visited on our website</li>
          <li>The search terms you use</li>
          <li>The links on which you click</li>
          <li>
            Personal information that has been made nonpersonal or anonymous
            (i.e., information that no longer reflects or references an
            individually identifiable user)
          </li>
        </ul>
        <p>
          If you visit our website to read or download information, such as
          information about a health condition or about one of our products, we
          may collect certain non-personal information about you from your
          computer. This information is collected from your computer's web
          browser. If you only read or download information on our website, we
          do not collect or learn your name, e-mail address, home address, or
          other personal information about you. We may place a text file called
          a cookie in the browser files of your computer when you visit. The
          cookie may be stored on your computer's hard drive. The information
          that we collect using cookies is non-personal information. You are
          always free to decline our cookies if your browser permits, but some
          parts of our website may not work properly for you if you do so. We
          will not allow third parties other than those companies that we hire
          to perform services or functions on our behalf to place cookies on or
          through our website.
        </p>
      </>
    ),
  },
  {
    id: "non-personal-use",
    heading: "What Does CCN Do with Non-Personal Information?",
    body: (
      <>
        <p>
          Because non-personal information cannot identify you or be tied to
          you, there are no restrictions on the ways that we can use or share
          non-personal information. We are always looking for ways to better
          serve you and improve this website. We will use non-personal
          information from you to help us make this website more useful to
          visitors. We also will use non-personal information for other
          business purposes. For example, we may use non-personal information
          or aggregate non-personal information to:
        </p>
        <ul>
          <li>
            Create reports for internal use to develop programs, products,
            services or content
          </li>
          <li>
            Customize the information or services that are of interest to you
          </li>
          <li>Share it with or sell it to third parties</li>
          <li>
            Provide aggregated information on how visitors use our site, such
            as "traffic statistics" and "response rates," to third parties
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "personal-info",
    heading: "What is Personal Information?",
    body: (
      <>
        <p>
          Personal information is information we collect or that you submit
          through this website (including through products that submit
          information to this website) that we can use to specifically identify
          you, for example, your:
        </p>
        <ul>
          <li>Name</li>
          <li>Date of birth</li>
          <li>Home address, telephone number</li>
          <li>E-mail address</li>
          <li>Protected health information (see below)</li>
        </ul>
        <p>
          In some places on this website, you have the opportunity to send us
          personal information about yourself, configure one of our products to
          send us personal information about yourself (and request for us to
          send such information to third parties), to elect to receive
          particular information, or to participate in an activity. For
          example, you may register on this website or may use a CCN product to
          enable your device to send your physiological readings to our site
          and then have us send the readings to a friend, family member or
          caregiver.
        </p>
        <p>
          You also may choose to allow us to personalize your visits to the
          website or personalize information we send to your device, in which
          case we will ask you for certain personal information to make your
          visits to our website more helpful to you. When this information is
          combined with the non-personal information that we collect through
          cookies (described above), we will be able to tell that you have
          visited our website before and can personalize your access to our
          website, for example, by telling you about new features that may be
          of interest to you or by notifying you if your meter readings are
          outside of a range which you provide.
        </p>
      </>
    ),
  },
  {
    id: "phi",
    heading: "Protected Health Information",
    body: (
      <>
        <p>
          "Protected health information" is information that may identify you
          and that relates to your physical or mental health or condition and
          related health care services. We are committed to safeguarding all
          protected health information collected about you, while providing
          health-related products, services, education and/or training.
          Examples of protected health information include:
        </p>
        <ul>
          <li>
            Information about your health condition (e.g., your blood pressure)
          </li>
          <li>Information about health care products or services provided to you</li>
          <li>Geographic information (e.g., your address)</li>
          <li>Demographic information (e.g., your race, gender, or age)</li>
          <li>
            Unique numbers that may identify you (e.g., your Social Security
            Number, phone number(s), or driver's license or state certificate
            number)
          </li>
          <li>Other types of information that may identify you</li>
        </ul>
      </>
    ),
  },
  {
    id: "personal-use",
    heading: "How Does CCN Keep and Use Personal Information?",
    body: (
      <>
        <p>
          We may keep and use personal information we collect from you through
          this website to provide you with access to this website. In addition,
          we may keep and use your personal information:
        </p>
        <ul>
          <li>To respond to your requests</li>
          <li>
            To send to third parties (family members, caregivers, friends,
            etc.) that you authorize CCN to send your personal information to.
          </li>
          <li>
            To share with our business partners for purposes of improving your
            experience with our products.
          </li>
          <li>
            To personalize your access to our website, for example, by telling
            you about new features that may be of interest to you
          </li>
          <li>To provide to caregivers such as your doctor or diabetes educator</li>
          <li>
            To personalize your device experience, for example, by notifying
            you of readings that are outside of a pre-defined range or
            notifying you of related products or features that may be of
            interest
          </li>
          <li>To develop records, including records of your personal information</li>
          <li>To contact you with information that might be of interest to you</li>
          <li>
            To remove your personal identifiers (your name, e-mail address,
            social security number, etc.). In this case, you would no longer be
            identified as a single unique individual. Once we have de-identified
            information, it is nonpersonal information and we may treat it like
            other non-personal information
          </li>
          <li>
            To enforce this Privacy Policy and other rules about your use of
            this website
          </li>
          <li>To protect our rights or property</li>
          <li>To protect someone's health, safety or welfare</li>
          <li>
            To provide you with treatment (i.e., health carerelated products,
            therapies, or services), and/or run our normal business operations.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "sharing",
    heading: "Does CCN Ever Share Personal Information with Third Parties?",
    body: (
      <>
        <p>
          CCN will not share your personal information collected from this
          website with an unrelated third party without your permission, except
          as otherwise provided in this Privacy Policy. CCN will share your
          information, including your physiological data test results and other
          information, with third parties that you authorize CCN to share this
          information with. By configuring your personal page on the website to
          send your personal information (including blood pressure test
          results) to third parties, or by using a meter provided or paid for
          by a third party such as an insurance company or benefits
          coordinator, you are consenting to CCN sharing your personal
          information with such third parties. Once your personal information
          is shared with a third party, CCN has no control over what such third
          party may do with your personal information or of any redisclosure of
          your personal information.
        </p>
        <p>
          In the ordinary course of business, we will share some personal
          information with companies that we hire to perform services or
          functions on our behalf. For example, we may use different vendors or
          suppliers to ship you products that you order on our website or may
          work with partners to improve your device experience. In these cases,
          we provide the vendor with information to process your order such as
          your name and mailing address. In all cases in which we share your
          personal information with a third party, we will not authorize them
          to keep, disclose or use your information with others except for the
          purpose of providing the services we asked them to provide. We will
          not sell, exchange or publish your personal information, except in
          conjunction with a corporate sale, merger, dissolution, or
          acquisition.
        </p>
        <p>
          We may be legally compelled to release your personal information in
          response to a court order, subpoena, search warrant, law or
          regulation. We may cooperate with law enforcement authorities in
          investigating and prosecuting website visitors who violate our rules
          or engage in behavior which is harmful to other visitors (or
          illegal).
        </p>
        <p>
          We may disclose your personal information to third parties if we feel
          that the disclosure is necessary to:
        </p>
        <ul>
          <li>
            To enforce this Privacy Policy and other rules about your use of
            this website
          </li>
          <li>To protect our rights or property</li>
          <li>To protect someone's health, safety or welfare</li>
          <li>
            To comply with a law or regulation, court order or other legal
            process
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "required-by-law",
    heading: "As Required By Law; Lawsuits and Other Proceedings.",
    body: (
      <p>
        We may disclose your personal information if required to do so by
        federal, state, or local law, or if ordered by a court or by another
        properly authorized body (e.g., in response to a subpoena, discovery
        request, or other legal request made by someone involved in the
        dispute, if we receive satisfactory assurances either that (1) you were
        notified of the request; or (2) the parties to the dispute have agreed
        to a qualified protective order regarding your personal information).
        We may disclose information in the context of civil litigation where
        you have put your condition at issue in the litigation.
      </p>
    ),
  },
  {
    id: "written-authorization",
    heading: "Written Authorization.",
    body: (
      <p>
        Except as described in this Privacy Policy, we will obtain your online
        or written authorization or consent before using your personal
        information or disclosing it to persons or organizations outside of CCN
        Also, we will obtain an authorization for any communications with you
        for marketing purposes, or any disclosures that constitute a sale of
        personal information. You may revoke any written authorization you have
        provided to us in writing, at any time. If you revoke your
        authorization, we will no longer use or disclose your personal
        information for the reasons covered by your written authorization,
        except to the extent that we have made any use(s) or disclosure(s) of
        your protected health information in reliance on an existing
        authorization. We are unable to take back any disclosures we have
        already made with your permission, and we are required to retain our
        records of the care that we have provided to you. To revoke an
        authorization, please send your request in writing with a copy of the
        authorization being revoked at the address below.
      </p>
    ),
  },
  {
    id: "please-note",
    heading: "Please Note",
    body: (
      <p>
        In addition to the ways that we may keep, disclose, and use information
        described in this Privacy Policy, we also may keep, disclose, and use
        personally identifiable information that you give us through this
        website in ways that we believe are consistent with FDA and other
        governmental guidance, directions, regulations, and laws.
      </p>
    ),
  },
  {
    id: "other-websites",
    heading: "What About Privacy on Other Websites?",
    body: (
      <p>
        This website may contain links to other websites. Some of those
        websites may be operated by CCN, and some may be operated by third
        parties. We provide the links for your convenience, but we do not
        review, control, or monitor the privacy practices of websites operated
        by others. This Privacy Policy does not apply to any other website,
        even the other CCN websites. We are not responsible for the performance
        of websites operated by third parties or for your business dealings
        with them. Therefore, whenever you leave this website we recommend that
        you review each website's privacy practices and make your own
        conclusions regarding the adequacy of these practices.
      </p>
    ),
  },
  {
    id: "communicate-directly",
    heading: "Does CCN Ever Communicate Directly with Visitors to This Website?",
    body: (
      <p>
        This website may contain links to other websites. Some of those
        websites may be operated by CCN, and some may be operated by third
        parties. We provide the links for your convenience, but we do not
        review, control, or monitor the privacy practices of websites operated
        by others. This Privacy Policy does not apply to any other website,
        even the other CCN websites. We are not responsible for the performance
        of websites operated by third parties or for your business dealings
        with them. Therefore, whenever you leave this website we recommend that
        you review each website's privacy practices and make your own
        conclusions regarding the adequacy of these practices.
      </p>
    ),
  },
  {
    id: "security",
    heading: "What About Website Security?",
    body: (
      <p>
        Security is important to us. We take reasonable steps to protect your
        personal information from loss, misuse, and unauthorized access,
        disclosure, alteration, or destruction. You should keep in mind that no
        Internet transmission is ever 100% secure or error-free. Particularly,
        e-mail sent to or from this site may not be secure, and you should
        therefore take special care in deciding what information you send to us
        via e-mail.
      </p>
    ),
  },
  {
    id: "contact-ccn",
    heading: "How to Contact CCN",
    body: (
      <p>
        If you have any questions, comments, or concerns about this Privacy
        Policy or the information practices of this site, please contact us as
        follows: E-mail:{" "}
        <a href="mailto:info@careconnectionnetworks.com">
          info@careconnectionnetworks.com
        </a>
        .
      </p>
    ),
  },
  {
    id: "changes",
    heading: "Changes to Privacy Policy",
    body: (
      <p>
        This Privacy Policy was last updated February, 2021. If this Privacy
        Policy is revised, the most current policy will be posted on this site.
        Please check back periodically, and especially before you provide any
        personally identifiable information.
      </p>
    ),
  },
];

const PrivacyPage = () => (
  <LegalPage
    title="Privacy Policy"
    updatedNote="Last updated February 2021"
    sections={SECTIONS}
    contact={
      <p>
        E-mail:{" "}
        <a href="mailto:info@careconnectionnetworks.com">
          info@careconnectionnetworks.com
        </a>
      </p>
    }
  />
);

export default PrivacyPage;
